// ── Stratification ────────────────────────────────────────────────────────
//
// One table, many cuts. Pick a dimension and the book regroups underneath it:
// by state, by facility type, by margin band, by remaining term. Every row
// expands to the accounts inside it, and every account expands to its loans, so
// a number you do not believe is two clicks from the loans that produced it.
//
// TWO BALANCES, AND THEY ARE NOT INTERCHANGEABLE:
//
//   LOAN BALANCE     what the borrower owes us — the whole facility.
//   BS LOAN BALANCE  what we own on our balance sheet, after participations.
//
// BS is therefore ALWAYS EQUAL TO OR LESS THAN Loan Balance. A loan with no
// participation shows the same number twice; a participated one shows a
// smaller BS balance.
//
// WEIGHTED AVERAGES AND AVG BS BAL ARE ON THE BS BALANCE, NOT ON COUNT. That
// is deliberate: a portfolio rate should be weighted by what we actually hold,
// not by what the borrower owes, because the participated portion earns
// somebody else's return. And a $40mm loan at 5% next to a $500k loan at 14%
// does not average to 9.5% in any sense a credit committee cares about. Where
// the BS balance is zero the average is undefined and shows as a dash rather
// than as zero, because zero is a number and "no basis" is not.
//
// TWO SOURCES, ONE SHAPE. With no as-of date the page reads getLoans — the
// book as it stands. With an as-of date it reads getReportData, which runs
// dbo.fnReportLoansAsOf(@date), the same historical loan function the report
// builder uses. Both are normalised into one row shape by the adapters below,
// so every aggregate, cut and column downstream is written once and neither
// source leaks its own field names into the page.
//
// THE TWO SOURCES USED TO NAME THE SECOND BALANCE DIFFERENTLY:
//
//                     Loan Balance            BS Loan Balance
//   getLoans          loanbalanceeod          bsloanbalanceeod
//   report function   loanbalanceeod          bsloanbalanceeod
//                                             (was aequumloanbalanceeod)
//
// The reporting view has since been renamed so both sources agree.
//
// THAT RENAME IS A PREREQUISITE FOR THIS PAGE, NOT AN OPTIONAL FOLLOW-UP. The
// as-of view asks getReportData for named columns and SQL fails the whole
// query — not the one column — if a name is not there, so on a database where
// the rename has not been applied the historical view returns an error rather
// than degrading. Apply the SQL first, deploy second. The SQL step that adds
// the new name is additive and safe to run against the live app.
//
// GET THE TWO ROUND THE RIGHT WAY. Loan Balance is what the borrower owes us;
// BS Loan Balance is what we hold on our own balance sheet, and is always the
// same or smaller. Swapping them produces no error, just every figure on the
// page reversed — which is why the adapters map to explicit `loanBal` and
// `bsBal` fields and nothing downstream touches a raw API field name.
//
// ORIGINAL TERM RUNS TO THE ORIGINAL MATURITY. `maturitydate` on both sources
// is the CURRENT maturity, off the latest funded term, so measuring original
// term against it reports an extended loan as having been written longer-dated
// than it was. getLoans now also returns `originalmaturitydate` — the maturity
// on the FIRST funded term — and that is what the Original Term column and cut
// use.
//
// The report function does not expose it yet: fnReportLoansAsOf and
// vReportLoans are database objects, not repo files, so the historical view
// leaves WA Orig Term blank rather than substituting the current maturity. The
// SQL to add it ships alongside this change; once it is applied the column
// fills in on its own with no further code change.

import { useState, useEffect, useMemo, Fragment } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE, SURFACE_MUTED, HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, FilterSelect, Btn, SearchBar } from "../../../shared/ui";
import { fmt, fmtRatio, fmtDate, exportCSV, localFilter } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";

const num = v => parseFloat(v || 0) || 0;
const LIVE_STATUSES = ["Accrual", "Non Accrual"];

// fundnames is a STRING_AGG — one loan can be allocated across several funds
// and arrives as "Fund I, Fund II". So the fund filter tests MEMBERSHIP rather
// than matching the whole string, or "Fund I" would miss every loan it shares
// with another fund.
//
// The Fund Ownership CUT deliberately still groups on the whole string: those
// groups are combinations of funds, and splitting a loan across two groups
// would double-count its balance in the totals. Filter by membership, group by
// combination.
const fundsOf = l => {
  const raw = l.fundnames;
  if (raw === null || raw === undefined || String(raw).trim() === "") return [];
  return String(raw).split(",").map(x => x.trim()).filter(Boolean);
};

// SQL dates as local dates — see the note in DrawForecastPage. Same trap.
const parseDate = d => {
  if (!d) return null;
  const s = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, day] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  // Date ROLLS OVER impossible days rather than failing: new Date(2026, 1, 31)
  // is 3 March, not an error. Round-tripping the parts is the only way to tell
  // "31 February" apart from a date that exists, and a hand-typed date box can
  // produce exactly that.
  if (isNaN(dt) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== day)
    return null;
  return dt;
};
const monthsBetween = (a, b) => {
  if (!a || !b) return null;
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
       + (b.getDate() >= a.getDate() ? 0 : -1);
};

// ── source adapters ───────────────────────────────────────────────────────
// Each returns the one row shape the rest of this file knows about. Nothing
// below here ever reads a raw API field name.
const REPORT_COLUMNS = [
  "loannumber", "companyname", "accountnumber", "state", "loanstatus",
  "RiskRating", "fundnames", "originalfundingdate", "maturitydate",
  "facilitytype", "collateraltype", "margin", "rateindex", "commitmentamount",
  // Present only once the view/TVF carries it; see ORIGINAL TERM below.
  "originalmaturitydate",
  // Requires the reporting view to carry bsloanbalanceeod — see the note at the
  // top on why this cannot fall back to the retired name.
  "loanbalanceeod", "bsloanbalanceeod", "ea_totalcollateralvalue",
  "excessavailability", "industry",
];

const fromGetLoans = l => ({
  key: String(l.id),
  // getLoans has a real account id; prefer it over a name that can repeat.
  acct: String(l.accountid),
  loannumber: l.loannumber, companyname: l.companyname, state: l.state,
  loanstatus: l.loanstatus, facilitytype: l.facilitytype, fundnames: l.fundnames,
  industry: l.industry, collateraltype: l.collateraltype, rateindex: l.rateindex,
  riskrating: l.RiskRating, margin: l.margin,
  maturitydate: l.maturitydate, originalfundingdate: l.originalfundingdate,
  originalmaturitydate: l.originalmaturitydate,
  commitment: num(l.commitmentamount),
  loanBal:    num(l.loanbalanceeod),      // owed by the borrower
  bsBal:      num(l.bsloanbalanceeod),    // owned on our balance sheet
  collateral: num(l.ea_totalcollateralvalue),
  excess:     num(l.excessavailability),
});

const fromReport = (r, i) => ({
  key: "h" + i,
  // The report function exposes no account id, so accounts are keyed on the
  // account NUMBER, which is what it does expose and what is unique.
  acct: String(r.accountnumber || r.companyname || "~unknown"),
  loannumber: r.loannumber, companyname: r.companyname, state: r.state,
  loanstatus: r.loanstatus, facilitytype: r.facilitytype, fundnames: r.fundnames,
  industry: r.industry, collateraltype: r.collateraltype, rateindex: r.rateindex,
  riskrating: r.RiskRating, margin: r.margin,
  maturitydate: r.maturitydate, originalfundingdate: r.originalfundingdate,
  // The report function does not expose it — see the note on ORIGINAL TERM.
  originalmaturitydate: r.originalmaturitydate ?? null,
  commitment: num(r.commitmentamount),
  loanBal:    num(r.loanbalanceeod),       // owed by the borrower
  bsBal:      num(r.bsloanbalanceeod),     // owned on our balance sheet
  collateral: num(r.ea_totalcollateralvalue),
  excess:     num(r.excessavailability),
});

// ── the metrics on a set of loans ─────────────────────────────────────────
// The weighting basis, in one place. Balance sheet balance: weight a portfolio
// rate by what we hold, not by what the borrower owes.
const WEIGHT = l => l.bsBal;

const agg = (rows, asOfDate) => {
  let commitment = 0, collateral = 0, bal = 0, bsBal = 0, excess = 0;
  let wMargin = 0, wOrig = 0, wRem = 0, wBase = 0, origBase = 0, remBase = 0;
  const accounts = new Set();
  // Remaining term is measured from the AS-OF DATE. A 2024 stratification
  // whose remaining terms were computed from today would report every loan
  // as further through its life than it was on the date being reported.
  const today = asOfDate || (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

  rows.forEach(l => {
    accounts.add(l.acct);
    const w = WEIGHT(l);
    commitment += l.commitment;
    collateral += l.collateral;
    bal        += l.loanBal;
    bsBal      += l.bsBal;
    excess     += l.excess;

    // A loan with no balance contributes to neither the numerator nor the
    // denominator — it cannot move a weighted average, and letting it into the
    // denominator would drag every rate toward zero.
    if (w > 0) {
      wBase += w;
      wMargin += num(l.margin) * w;

      const mat  = parseDate(l.maturitydate);
      const orig = parseDate(l.originalfundingdate);
      // ORIGINAL term runs to the ORIGINAL maturity — the one on the first
      // funded term — not to today's maturity. An extended loan would
      // otherwise report an origination term it never had. Where the original
      // maturity is unavailable the loan is left out of the average entirely
      // rather than silently substituting the current maturity, which is the
      // wrong number wearing the right label.
      const ot = monthsBetween(orig, parseDate(l.originalmaturitydate));
      if (ot !== null) { wOrig += ot * w; origBase += w; }
      const rt = monthsBetween(today, mat);
      if (rt !== null) { wRem += rt * w; remBase += w; }
    }
  });

  return {
    accounts: accounts.size, loans: rows.length,
    commitment, collateral, bal, bsBal, excess,
    avgBal: rows.length ? bsBal / rows.length : 0,
    waMargin: wBase   ? wMargin / wBase   : null,
    waOrig:   origBase ? wOrig  / origBase : null,
    waRem:    remBase  ? wRem   / remBase  : null,
  };
};

// ── the cuts ──────────────────────────────────────────────────────────────
// A band returns { key, label, sort } so buckets order by magnitude rather
// than alphabetically — "10–25mm" must not sort between "1–5mm" and "5–10mm".
const band = (v, stops, fmtLabel) => {
  if (v === null || v === undefined || v === "" || isNaN(Number(v)))
    return { key: "~none", label: "Not set", sort: Infinity };
  const n = Number(v);
  for (let i = 0; i < stops.length; i++) {
    if (n < stops[i]) return { key: "b" + i, label: fmtLabel(i), sort: i };
  }
  return { key: "b" + stops.length, label: fmtLabel(stops.length), sort: stops.length };
};
const plain = (v, label) => (v === null || v === undefined || String(v).trim() === "")
  ? { key: "~none", label: label || "Not set", sort: Infinity }
  : { key: String(v), label: String(v), sort: null };

const MM = [1e6, 5e6, 10e6, 25e6];
const MM_LABELS = ["Under $1mm", "$1–5mm", "$5–10mm", "$10–25mm", "$25mm+"];
const PCT = [0.05, 0.06, 0.07, 0.08];
const PCT_LABELS = ["Under 5.00%", "5.00–6.00%", "6.00–7.00%", "7.00–8.00%", "8.00%+"];
const OT = [13, 25, 37, 61];
const OT_LABELS = ["12 months or less", "13–24 months", "25–36 months", "37–60 months", "Over 60 months"];

const today0 = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
// Local calendar date as YYYY-MM-DD, so the date input cannot offer a
// "tomorrow" that is only tomorrow in UTC.
const todayISO = (() => { const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0")
       + "-" + String(d.getDate()).padStart(2,"0"); })();

const DIMENSIONS = [
  { id:"account",   label:"Account",         of: l => plain(l.companyname, "(no name)") },
  { id:"state",     label:"State",           of: l => plain(l.state, "No state on record") },
  { id:"facility",  label:"Facility Type",   of: l => plain(l.facilitytype) },
  { id:"status",    label:"Loan Status",     of: l => plain(l.loanstatus) },
  { id:"fund",      label:"Fund Ownership",  of: l => plain(l.fundnames, "No fund on record") },
  { id:"industry",  label:"Industry",        of: l => plain(l.industry) },
  { id:"collateral",label:"Collateral Type", of: l => plain(l.collateraltype) },
  { id:"index",     label:"Rate Index",      of: l => plain(l.rateindex) },
  { id:"risk",      label:"Risk Rating",     of: l => {
      const r = l.riskrating;
      if (r === null || r === undefined || r === "") return { key:"~none", label:"Unrated", sort:Infinity };
      return { key:String(r), label:"Rating " + r, sort:Number(r) };
    } },
  { id:"margin",    label:"Margin",          of: l => band(l.margin, PCT, i => PCT_LABELS[i]) },
  { id:"balance",   label:"BS Loan Balance", of: l => band(l.bsBal, MM, i => MM_LABELS[i]) },
  { id:"origterm",  label:"Original Term",   of: l => {
      // First funded term's maturity, not the current one — see agg().
      const m = monthsBetween(parseDate(l.originalfundingdate),
                              parseDate(l.originalmaturitydate));
      return band(m, OT, i => OT_LABELS[i]);
    } },
  { id:"remterm",   label:"Remaining Term",  of: (l, asOf) => {
      // Measured from the as-of date, like the WA Rem Term column. A loan
      // that had 18 months left in 2024 belongs in the 13-24 bucket of a
      // 2024 stratification, not in whatever bucket it sits in today.
      const m = monthsBetween(asOf || today0(), parseDate(l.maturitydate));
      if (m === null) return { key:"~none", label:"No maturity date", sort:Infinity };
      if (m < 0)  return { key:"past",  label:"Past maturity",   sort:-1 };
      if (m <= 6) return { key:"b0",    label:"6 months or less", sort:0 };
      if (m <= 12) return { key:"b1",   label:"7–12 months",      sort:1 };
      if (m <= 24) return { key:"b2",   label:"13–24 months",     sort:2 };
      return { key:"b3", label:"Over 24 months", sort:3 };
    } },
];

// ── columns ───────────────────────────────────────────────────────────────
// Headings follow ACE — "Loan Balance" and "BS Loan Bal" — and the report
// builder now uses the same pair. It used to have its own two names for the
// same two figures, which is how a reconciliation meeting starts.
// What each column means, shown on hover of its heading. This used to be a
// paragraph above the table; it reads better here, at the moment somebody
// doubts a specific number, than as a block nobody re-reads after the first
// visit.
const COL_HELP = {
  accounts:   "Distinct accounts in this group. An account with loans in two groups is counted in each, so this column does not sum to the total.",
  loans:      "Loans in this group.",
  pct:        "Share of Loan Balance — what borrowers owe — matching the Power BI report. Not a share of BS Loan Bal.",
  commitment: "Committed amount.",
  collateral: "Net of inventory sublimit and reserve and scaled by participation — the same basis as the dashboard's Collateral column.",
  bal:        "What the borrower owes us: the whole facility.",
  bsBal:      "What we own on our balance sheet, after participations. Always the same as Loan Balance or smaller.",
  excess:     "Excess availability.",
  avgBal:     "BS Loan Bal \u00f7 loan count.",
  waMargin:   "Weighted by BS Loan Bal — a portfolio rate should reflect what we hold, not what the borrower owes. Loans with no BS balance are excluded from both sides of the weighting, so a group with none behind it shows a dash rather than 0.00%.",
  waOrig:     "Months from the original funding date to the maturity on the FIRST funded term, so an extended loan is not reported as longer-dated at origination than it was. Weighted by BS Loan Bal.",
  waRem:      "Months from the as-of date (or today) to the CURRENT maturity. Negative past maturity — that is a real state, not a data error. Weighted by BS Loan Bal.",
};

const COLS = [
  { id:"accounts",  label:"Accts",         get:m => m.accounts,   fmt:v => v,             num:true },
  { id:"loans",     label:"Loans",         get:m => m.loans,      fmt:v => v,             num:true },
  { id:"pct",       label:"% of Bal",      get:m => m.pct,        fmt:v => fmtRatio(v,1), num:true },
  { id:"commitment",label:"Commitment",    get:m => m.commitment, fmt:fmt,                num:true },
  { id:"collateral",label:"Collateral",    get:m => m.collateral, fmt:fmt,                num:true },
  { id:"bal",       label:"Loan Balance",  get:m => m.bal,        fmt:fmt,                num:true },
  { id:"bsBal",     label:"BS Loan Bal",   get:m => m.bsBal,      fmt:fmt,                num:true },
  { id:"excess",    label:"Excess Avail",  get:m => m.excess,     fmt:fmt,                num:true },
  { id:"avgBal",    label:"Avg BS Bal",    get:m => m.avgBal,     fmt:fmt,                num:true },
  { id:"waMargin",  label:"WA Margin",     get:m => m.waMargin,   fmt:v => v === null ? "—" : fmtRatio(v), num:true },
  { id:"waOrig",    label:"WA Orig Term",  get:m => m.waOrig,     fmt:v => v === null ? "—" : v.toFixed(0), num:true },
  { id:"waRem",     label:"WA Rem Term",   get:m => m.waRem,      fmt:v => v === null ? "—" : v.toFixed(0), num:true },
];

export const StratificationPage = () => {
  const { api } = useApp();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dim, setDim] = useState("state");
  const [activeOnly, setActiveOnly] = useState(true);
  const [fund, setFund] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const [sort, setSort] = useState({ col:"bsBal", dir:"desc" });
  // asOf is COMMITTED and drives the query. asOfDraft is whatever the date
  // input currently holds, including half-typed and arrowed-through values.
  // Keeping them apart is what stops a spinner click from firing a query.
  const [asOf, setAsOf] = useState("");
  const [asOfDraft, setAsOfDraft] = useState("");
  const [rates, setRates] = useState([]);

  const asOfDate = useMemo(() => parseDate(asOf), [asOf]);

  // A draft is committable when it is empty (back to today) or a complete,
  // real, non-future date. A native date input reports "" while a segment is
  // still blank, and browsers that allow typing can produce 2026-02-31, which
  // parseDate rejects by round-tripping through Date.
  const EARLIEST_ASOF = "2000-01-01";
  const draftValid = asOfDraft === "" || (() => {
    const d = parseDate(asOfDraft);
    return !!d && asOfDraft <= todayISO && asOfDraft >= EARLIEST_ASOF;
  })();
  const pendingAsOf = asOfDraft !== asOf && draftValid;

  const commitAsOf = () => {
    if (!draftValid) { setAsOfDraft(asOf); return; }   // discard nonsense, keep the view
    if (asOfDraft !== asOf) setAsOf(asOfDraft);
  };

  const load = () => {
    setLoading(true);
    setErr(null);
    const source = asOf
      ? api.post("getReportData", { source:"loans", columns: REPORT_COLUMNS,
                                    asofdate: asOf, limit: 100000 })
          .then(d => (Array.isArray(d && d.rows) ? d.rows : []).map(fromReport))
      : api.get("getLoans")
          .then(d => (Array.isArray(d) ? d : []).map(fromGetLoans));
    source
      .then(rows => { setLoans(rows); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  // Reloads when the as-of date changes: the two sources are different queries,
  // not a client-side filter over one dataset.
  useEffect(() => { load(); }, [asOf]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("getRates", { indexname: "SOFR" })
      .then(d => setRates(Array.isArray(d) ? d : []))
      .catch(() => setRates([]));   // the rate card is context, never a blocker
  }, [api]);

  // The rate in effect on the stratification date: the most recent published
  // SOFR on or before it. Rates are not published every calendar day, so
  // "the rate ON this date" would be blank most weekends and holidays.
  const sofr = useMemo(() => {
    const target = asOfDate || (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
    let best = null;
    rates.forEach(r => {
      const d = parseDate(r.date);
      if (!d || d > target) return;
      if (!best || d > best.d) best = { d, rate: r.rate, source: r.source };
    });
    return best;
  }, [rates, asOfDate]);

  // Cannot happen if the source adapters map the two balance columns the right
  // way round, so if it does, the mapping is wrong and every figure is the
  // wrong one. Cheap to check, and silent failure here is expensive.
  // Individual funds, not the combinations. Loans carrying no fund name are not
  // offered as a bucket — every funded loan is allocated, so a blank there is a
  // data gap, not a category worth filtering to.
  const fundOptions = useMemo(() => {
    const set = new Set();
    loans.forEach(l => fundsOf(l).forEach(x => set.add(x)));
    return [...set].sort().map(f => ({ value:f, label:f }));
  }, [loans]);

  // If the loaded set no longer contains the selected fund — the as-of date
  // moved, or the filter changed — fall back to all rather than showing an
  // empty table under a fund name that is not there.
  useEffect(() => {
    if (fund && !fundOptions.some(o => o.value === fund)) setFund("");
  }, [fund, fundOptions]);

  const resolvedCount = useMemo(
    () => loans.filter(l => !LIVE_STATUSES.includes(l.loanstatus)).length, [loans]);

  const rows = useMemo(() => {
    let r = activeOnly ? loans.filter(l => LIVE_STATUSES.includes(l.loanstatus)) : loans;
    if (fund) r = r.filter(l => fundsOf(l).includes(fund));
    return localFilter(r, q, ["companyname", "loannumber", "state", "facilitytype",
                              "loanstatus", "industry", "fundnames"]);
  }, [loans, activeOnly, fund, q]);

  const dimension = DIMENSIONS.find(d => d.id === dim) || DIMENSIONS[0];

  // ── the tree ────────────────────────────────────────────────────────────
  // group → account → loan. When the cut IS account the middle level would
  // repeat the top one, so it collapses to account → loan.
  const tree = useMemo(() => {
    const groups = new Map();
    rows.forEach(l => {
      const g = dimension.of(l, asOfDate);
      if (!groups.has(g.key)) groups.set(g.key, { ...g, rows: [] });
      groups.get(g.key).rows.push(l);
    });

    const total = agg(rows, asOfDate);
    const denom = total.bal;

    const withPct = m => ({ ...m, pct: denom ? m.bal / denom : 0 });

    const nodes = [...groups.values()].map(g => {
      const m = withPct(agg(g.rows, asOfDate));
      let children;
      if (dim === "account") {
        children = g.rows.map(l => ({
          id: "loan:" + l.key, kind: "loan", loan: l,
          label: l.loannumber || "(no number)",
          metrics: withPct(agg([l], asOfDate)),
        }));
      } else {
        const byAcct = new Map();
        g.rows.forEach(l => {
          const k = l.acct;
          if (!byAcct.has(k)) byAcct.set(k, { id:"acct:"+g.key+":"+k, kind:"account",
                                             label: l.companyname || "(no name)",
                                             rows: [] });
          byAcct.get(k).rows.push(l);
        });
        children = [...byAcct.values()].map(a => ({
          ...a,
          metrics: withPct(agg(a.rows, asOfDate)),
          children: a.rows.map(l => ({
            id: "loan:" + l.key, kind: "loan", loan: l,
            label: l.loannumber || "(no number)",
            metrics: withPct(agg([l], asOfDate)),
          })),
        }));
      }
      return { id: "grp:" + g.key, kind: "group", label: g.label, sortHint: g.sort,
               metrics: m, children };
    });

    return { nodes, total: withPct(total) };
  }, [rows, dim, dimension, asOfDate]);

  // Bands sort by magnitude; everything else sorts by the chosen column. A
  // margin table that runs "5–6%, 8%+, under 5%" is a list, not a distribution.
  const sortedNodes = useMemo(() => {
    const banded = tree.nodes.some(n => n.sortHint !== null && n.sortHint !== undefined);
    const col = COLS.find(c => c.id === sort.col);
    const cmp = (a, b) => {
      if (banded) return (a.sortHint ?? Infinity) - (b.sortHint ?? Infinity);
      const av = col ? col.get(a.metrics) : 0, bv = col ? col.get(b.metrics) : 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === "string") return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === "asc" ? av - bv : bv - av;
    };
    const out = [...tree.nodes].sort(cmp);
    out.forEach(n => {
      if (!n.children) return;
      n.children.sort((a, b) => (b.metrics.bsBal - a.metrics.bsBal));
      n.children.forEach(c => c.children && c.children.sort((x, y) => y.metrics.bsBal - x.metrics.bsBal));
    });
    return out;
  }, [tree, sort]);

  const toggle = id => setOpen(prev => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const expandAll = () => {
    const s = new Set();
    sortedNodes.forEach(n => { s.add(n.id); (n.children||[]).forEach(c => c.children && s.add(c.id)); });
    setOpen(s);
  };
  const collapseAll = () => setOpen(new Set());

  // Changing the cut invalidates every open row id, so start collapsed rather
  // than leaving stale ids that match nothing.
  useEffect(() => { setOpen(new Set()); }, [dim]);

  // Deliberately NOT an early return. Unmounting the whole page on every fetch
  // would tear the date input out of the DOM mid-interaction and drop focus, so
  // the controls stay put and the table area shows the spinner instead.
  const busy = loading;

  const th = { textAlign:"right", padding:"8px 10px", fontSize:11, fontWeight:600,
               color:INK_MUTED, background:SURFACE_MUTED, whiteSpace:"nowrap",
               borderBottom:"1px solid "+HAIRLINE, cursor:"pointer", userSelect:"none",
               position:"sticky", top:0, zIndex:1 };
  const cellNum = { textAlign:"right", padding:"7px 10px", fontSize:12,
                    fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };

  const renderRow = (n, depth) => {
    const isOpen = open.has(n.id);
    const hasKids = !!(n.children && n.children.length);
    const shade = depth === 0 ? "transparent" : depth === 1 ? SURFACE_MUTED : "transparent";
    const weight = depth === 0 ? 600 : depth === 1 ? 500 : 400;
    // Keyed on the Fragment, not the <tr>: the fragment is what the parent
    // array actually holds, so a key on the row inside it is not a key at all.
    return (
      <Fragment key={n.id}>
        <tr style={{ background: shade, borderBottom:"0.5px solid "+HAIRLINE,
                     cursor: hasKids ? "pointer" : "default" }}
            onClick={hasKids ? () => toggle(n.id) : undefined}>
          <td style={{ padding:"7px 10px", fontSize:12, fontWeight:weight,
                       paddingLeft: 10 + depth * 18, whiteSpace:"nowrap",
                       position:"sticky", left:0, zIndex:1,
                       background: depth === 1 ? SURFACE_MUTED : SURFACE }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
              <span style={{ width:11, display:"inline-block", color:INK_MUTED, fontSize:10 }}>
                {hasKids ? (isOpen ? "▾" : "▸") : ""}
              </span>
              {n.kind === "loan" ? (
                <span style={{ fontFamily:"var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace)",
                               fontSize:11.5 }}>{n.label}</span>
              ) : n.label}
              {n.kind === "loan" && n.loan.maturitydate && (
                <span style={{ color:INK_MUTED, fontSize:11 }}>
                  · {n.loan.facilitytype || "—"} · matures {fmtDate(n.loan.maturitydate)}
                </span>
              )}
            </span>
          </td>
          {COLS.map(c => (
            <td key={c.id} style={{ ...cellNum, fontWeight: depth === 0 ? 600 : 400 }}>
              {c.fmt(c.get(n.metrics))}
            </td>
          ))}
        </tr>
        {isOpen && hasKids && n.children.map(c => renderRow(c, depth + 1))}
      </Fragment>
    );
  };

  const flatForExport = () => {
    const out = [];
    const walk = (n, depth, path) => {
      out.push({
        level: depth === 0 ? dimension.label : depth === 1 ? "Account" : "Loan",
        group: path.concat(n.label).join(" › "),
        name: n.label,
        accounts: n.metrics.accounts, loans: n.metrics.loans,
        pct_of_balance: (n.metrics.pct * 100).toFixed(2),
        commitment: n.metrics.commitment.toFixed(2),
        collateral: n.metrics.collateral.toFixed(2),
        loan_balance: n.metrics.bal.toFixed(2),
        bs_loan_balance: n.metrics.bsBal.toFixed(2),
        excess_availability: n.metrics.excess.toFixed(2),
        avg_loan_balance: n.metrics.avgBal.toFixed(2),
        wa_margin: n.metrics.waMargin === null ? "" : (n.metrics.waMargin * 100).toFixed(4),
        wa_orig_term_months: n.metrics.waOrig === null ? "" : n.metrics.waOrig.toFixed(1),
        wa_rem_term_months: n.metrics.waRem === null ? "" : n.metrics.waRem.toFixed(1),
      });
      (n.children || []).forEach(c => walk(c, depth + 1, path.concat(n.label)));
    };
    sortedNodes.forEach(n => walk(n, 0, []));
    return out;
  };

  const t = tree.total;

  return (
    <>
      <Card style={{ padding:14, marginBottom:14 }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"flex-end" }}>
          <FilterSelect label="Stratify by" value={dim} onChange={setDim}
                        options={DIMENSIONS.map(d => ({ value:d.id, label:d.label }))}
                        allLabel={null} />
          {/* Empty means today's book. A date runs the historical report
              function, so every balance, collateral figure and remaining term
              is computed as it stood on that date.
              ── THE INPUT IS DELIBERATELY NOT THE SOURCE OF TRUTH ──
              A native date input fires `change` on every spinner tick, so
              arrowing the month from September back to March fires six times
              and each one is a full historical query against the report
              function. Worse, every intermediate month renders as if it were
              the answer. The typed value lives in `asOfDraft`; only an
              explicit commit — blur, Enter, or Apply — moves it to `asOf`,
              which is the only thing the fetch depends on. Arrow away; nothing
              runs until you mean it. */}
          <label style={{ display:"flex", flexDirection:"column", gap:3, fontSize:11,
                          color:INK_MUTED, textTransform:"uppercase", letterSpacing:"0.05em",
                          fontWeight:600 }}>
            As of
            <span style={{ display:"flex", gap:4, alignItems:"center" }}>
              <input type="date" value={asOfDraft} max={todayISO} min={EARLIEST_ASOF}
                     onChange={e => setAsOfDraft(e.target.value)}
                     onBlur={commitAsOf}
                     onKeyDown={e => {
                       if (e.key === "Enter") { e.preventDefault(); commitAsOf(); }
                       if (e.key === "Escape") { e.preventDefault(); setAsOfDraft(asOf); }
                     }}
                     title="Pick or arrow to a date, then press Enter, click Apply, or click away."
                     style={{ padding:"6px 8px", fontSize:12, fontFamily:"inherit",
                              borderRadius:6, background:SURFACE, color:INK_PRIMARY,
                              textTransform:"none", letterSpacing:0, fontWeight:400,
                              border:"1px solid " + (pendingAsOf ? B.amber : HAIRLINE_FIRM),
                              outline: pendingAsOf ? "1px solid "+B.amber : "none" }} />
              {/* Only appears while the draft differs from what is on screen,
                  so it doubles as the signal that a change is pending. */}
              {pendingAsOf && (
                <button type="button" onMouseDown={e => e.preventDefault()} onClick={commitAsOf}
                        title={"Run the stratification as of "
                             + (asOfDraft ? fmtDate(asOfDraft) : "today")}
                        style={{ padding:"5px 9px", fontSize:11, cursor:"pointer", fontWeight:600,
                                 borderRadius:6, border:"1px solid "+B.amber,
                                 background:B.amberLight, color:B.amberDark, fontFamily:"inherit",
                                 textTransform:"none", letterSpacing:0 }}>
                  Apply
                </button>
              )}
              {(asOf || asOfDraft) && !pendingAsOf && (
                <button type="button" onClick={() => { setAsOfDraft(""); setAsOf(""); }}
                        title="Back to today's book"
                        style={{ padding:"5px 8px", fontSize:11, cursor:"pointer",
                                 borderRadius:6, border:"1px solid "+HAIRLINE_FIRM,
                                 background:SURFACE, color:INK_MUTED, fontFamily:"inherit",
                                 textTransform:"none", letterSpacing:0, fontWeight:400 }}>
                  Today
                </button>
              )}
            </span>
          </label>
          <FilterSelect label="Fund" value={fund} onChange={setFund}
                        options={fundOptions} allLabel="All funds"
                        disabled={!fundOptions.length} />
          <SearchBar value={q} onChange={setQ} placeholder="Search loans, accounts, states..." />
          <label style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12,
                          paddingBottom:2, whiteSpace:"nowrap",
                          opacity: resolvedCount > 0 ? 1 : 0.45,
                          cursor: resolvedCount > 0 ? "pointer" : "default" }}
                 title={resolvedCount === 0
                      ? "Every loan is Accrual or Non Accrual — nothing to filter."
                      : `Hides loans that are not Accrual or Non Accrual — paid off, charged off, `
                        + `terminated, or not yet funded. ${resolvedCount} loan`
                        + `${resolvedCount === 1 ? "" : "s"} affected.`}>
            <input type="checkbox" checked={activeOnly} disabled={resolvedCount === 0}
                   onChange={e => setActiveOnly(e.target.checked)} />
            Active loans only{resolvedCount > 0 ? ` (${resolvedCount} hidden)` : ""}
          </label>
          <div style={{ display:"flex", gap:6, marginLeft:"auto" }}>
            <Btn onClick={expandAll}   disabled={!sortedNodes.length}>Expand all</Btn>
            <Btn onClick={collapseAll} disabled={!open.size}>Collapse all</Btn>
            <Btn onClick={() => exportCSV(flatForExport(),
                   ["level","group","name","accounts","loans","pct_of_balance","commitment",
                    "collateral","loan_balance","bs_loan_balance","excess_availability",
                    "avg_loan_balance","wa_margin","wa_orig_term_months","wa_rem_term_months"],
                   "stratification-" + dim + (fund ? "-" + fund.replace(/[^a-z0-9]+/gi,"-") : "")
                   + (asOf ? "-asof-" + asOf : "") + ".csv")}
                 disabled={!sortedNodes.length}>Export CSV</Btn>
          </div>
        </div>
      </Card>


      {err && <ErrorMsg msg={err} onRetry={load} />}

      {/* Notes on the left, the rate on the right, one band across the page.
          The rate belongs beside the as-of note because it IS as-of context:
          reading one without the other is how a historical stratification gets
          compared against today's cost of funds. */}
      <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:14,
                    flexWrap:"wrap" }}>
        {/* min-width:0 lets this column actually shrink and wrap its text
            instead of overflowing under the card beside it, which is what a
            flex item does by default. */}
        <div style={{ flex:"1 1 520px", minWidth:0, display:"flex",
                      flexDirection:"column", gap:10 }}>
        {/* Blank is the honest answer here, but only if it says why. */}
        {asOf && !loans.some(l => l.originalmaturitydate) && loans.length > 0 && (
          <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                        borderRadius:8, background:B.amberLight,
                        borderLeft:"3px solid "+B.amber, fontSize:12, lineHeight:1.6,
                        color:INK_PRIMARY }}>
            <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">ⓘ</span>
            <span>
              <strong>WA Orig Term is blank on historical views.</strong> Original term is measured
              to the maturity on the first funded term, and the historical report function does not
              return that column yet. Substituting the current maturity would report every extended
              loan as longer-dated at origination than it was, so it is left blank instead. Adding
              {" "}<span style={{ fontFamily:"var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace)" }}>
                originalmaturitydate</span>{" "}to vReportLoans and fnReportLoansAsOf fills it in
              with no further change here.
            </span>
          </div>
        )}

        {fund && (
          <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                        borderRadius:8, background:SURFACE_MUTED,
                        borderLeft:"3px solid "+B.cyan, fontSize:12, lineHeight:1.6,
                        color:INK_PRIMARY }}>
            <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">ⓘ</span>
            <span>
              <strong>{fund + " only."}</strong>
              {" "}Every figure below is that slice, not the whole book.
              {" "}A loan shared with another fund is included in full —
              these are loan balances, not each fund's share of them.
            </span>
          </div>
        )}

        {/* The as-of date changes which query ran, not just which rows show, so
            it says so on the page rather than only in a date box. */}
        {asOf && (
          <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                        borderRadius:8, background:SURFACE_MUTED,
                        borderLeft:"3px solid "+B.cyan, fontSize:12, lineHeight:1.6,
                        color:INK_PRIMARY }}>
            <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">ⓘ</span>
            <span>
              <strong>Historical view — the book as of {fmtDate(asOf)}.</strong> Balances,
              collateral, excess availability and commitments are the values that stood on that
              date, and remaining term is measured from it, not from today. Loan status is also
              as of that date, so the active-loans filter reflects what was live then.
            </span>
          </div>
        )}

        </div>

      {/* ── SOFR on the stratification date ────────────────────────────────
          Rates.rate is a DECIMAL RATIO (0.0367), not percent units, despite
          the rate form labelling the field "Rate (%)". fmtRatio multiplies by
          100; fmtPerc would render 3.67% as "0.0367%". Same trap as
          Terms.margin — when in doubt, check a real row, not the label.
          Deliberately the rate IN EFFECT on that date, not the rate stamped
          with it: SOFR is not published on weekends or holidays, so an exact
          date match would show a blank on roughly a third of the dates anyone
          would pick. The card says which date the rate actually came from so
          the substitution is never silent. */}
      <Card style={{ padding:"12px 16px", flex:"0 0 210px", marginBottom:0, alignSelf:"flex-start",
                     borderLeft:"3px solid "+B.cyan }}>
        <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase",
                      letterSpacing:"0.06em", color:INK_MUTED, marginBottom:6 }}>
          SOFR Rate
        </div>
        <div style={{ fontSize:20, fontWeight:600, fontVariantNumeric:"tabular-nums" }}>
          {sofr ? fmtRatio(num(sofr.rate)) : "—"}
        </div>
        <div style={{ fontSize:11, color:INK_MUTED, marginTop:3, lineHeight:1.45 }}>
          {sofr
            ? <>as of {fmtDate(sofr.d.toISOString().slice(0,10))}
                {asOf && sofr.d < asOfDate
                  ? <span title={"No SOFR published on " + fmtDate(asOf)
                               + ". Showing the most recent rate on or before it."}>
                      {" "}· last before {fmtDate(asOf)}
                    </span>
                  : null}</>
            : rates.length
              ? <span title="There is a SOFR series, but nothing published on or before this date.">
                  none published on or before {fmtDate(asOf || todayISO)}
                </span>
              : "no SOFR rates on file"}
        </div>
      </Card>
      </div>

      {/* THE SCROLLER MUST NOT HAVE PADDING.
          Card carries padding:16. With overflow on the Card itself, two things
          happen that together leave a permanent gap on the left:
            - overflow clips at the PADDING box, so horizontally scrolled
              content stays visible in that 16px strip;
            - a sticky cell is confined to its containing block, whose left
              edge is the CONTENT box — 16px further in. It can never reach
              across to cover the strip.
          So the frozen first column reads as transparent no matter what
          background it is given. Moving the scroll to a padding-free inner div
          puts the two edges in the same place and the sticky cell covers
          everything to its left. */}
      <Card style={{ padding:0, overflow:"hidden", position:"relative",
                     opacity: busy ? 0.45 : 1, transition:"opacity 120ms" }}>
       <div style={{ overflow:"auto", maxHeight:"70vh" }}>
        {busy && (
          <div style={{ position:"sticky", top:0, left:0, zIndex:5, padding:"24px 0" }}>
            <Spinner />
          </div>
        )}
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign:"left", left:0, zIndex:2, minWidth:280 }}
                  onClick={() => setSort(s => ({ col:"label", dir: s.dir === "asc" ? "desc" : "asc" }))}>
                {dimension.label}
              </th>
              {COLS.map(c => (
                <th key={c.id} style={th}
                    title={COL_HELP[c.id]}
                    onClick={() => setSort(s => ({ col:c.id,
                                                   dir: s.col === c.id && s.dir === "desc" ? "asc" : "desc" }))}>
                  {c.label}{sort.col === c.id ? (sort.dir === "desc" ? " ▾" : " ▴") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedNodes.map(n => renderRow(n, 0))}
            {!sortedNodes.length && (
              <tr><td colSpan={COLS.length + 1}
                      style={{ padding:36, textAlign:"center", fontSize:13, color:INK_MUTED }}>
                No loans match{q ? " that search" : ""}.
              </td></tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan,
                         position:"sticky", bottom:0 }}>
              <td style={{ padding:"9px 10px", fontSize:12, fontWeight:700, color:B.cyanDark,
                           position:"sticky", left:0, background:B.cyanLight, whiteSpace:"nowrap" }}>
                Total — {sortedNodes.length} {dimension.label.toLowerCase()}
                {sortedNodes.length === 1 ? "" : "s"}
              </td>
              {COLS.map(c => (
                <td key={c.id} style={{ ...cellNum, fontWeight:700, color:B.cyanDark }}>
                  {c.fmt(c.get(t))}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
       </div>
      </Card>
    </>
  );
};
